import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2, Film, Paperclip } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { fileUrl, formatApiError } from "../../lib/api";
import { uploadInChunks } from "../../lib/upload";
import ScreenshotMarkup from "./ScreenshotMarkup";

const MAX_MB = 200;
const kindOf = (a) => (a.content_type || "").startsWith("video/") ? "video" : (a.content_type || "").startsWith("image/") ? "image" : "file";

export function BugMediaPicker({ value = [], onChange, testid = "bug-media" }) {
  const ref = useRef();
  const [progress, setProgress] = useState(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const busyRef = useRef(false);
  const [queue, setQueue] = useState([]);

  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    if (ref.current) ref.current.value = "";
    await addFiles(files);
  };

  useEffect(() => {
    const onPaste = (e) => {
      const files = [...(e.clipboardData?.items || [])].filter(i => i.kind === "file" && i.type.startsWith("image/")).map(i => i.getAsFile()).filter(Boolean);
      if (!files.length) return;
      e.preventDefault();
      if (busyRef.current) { toast.error("Wait for the current upload to finish"); return; }
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      addFiles(files.map((f, i) => new File([f], `screenshot-${stamp}${i ? `-${i}` : ""}.${(f.type.split("/")[1] || "png").replace("jpeg", "jpg")}`, { type: f.type })));
    };
    document.addEventListener("paste", onPaste);
    return () => { document.removeEventListener("paste", onPaste); };
  }, []);

  const addFiles = (files) => {
    const ok = files.filter(f => {
      if (!/^(image|video)\//.test(f.type)) { toast.error(`${f.name}: only screenshots or screen recordings`); return false; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name}: max ${MAX_MB} MB`); return false; }
      return true;
    });
    setQueue(q => [...q, ...ok.filter(f => f.type.startsWith("image/"))]);
    const videos = ok.filter(f => f.type.startsWith("video/"));
    if (videos.length) upload(videos);
  };

  const nextInQueue = () => setQueue(q => q.slice(1));
  const attachMarked = (f) => { nextInQueue(); upload([f]); };

  const upload = async (files) => {
    busyRef.current = true;
    let next = [...valueRef.current];
    for (const f of files) {
      try {
        setProgress({ name: f.name, pct: 0 });
        next = [...next, await uploadInChunks(f, (pct) => setProgress({ name: f.name, pct }))];
        valueRef.current = next;
        onChange(next);
      } catch (err) { toast.error(`${f.name}: ${formatApiError(err.response?.data?.detail) || "upload failed"}`); }
    }
    setProgress(null);
    busyRef.current = false;
  };

  return (
    <div className="space-y-2">
      <input ref={ref} type="file" hidden multiple accept="image/*,video/*" onChange={onFiles} data-testid={`${testid}-input`} />
      <Button type="button" size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={!!progress} data-testid={`${testid}-btn`}>
        {progress ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5 mr-1" />}
        {progress ? `Uploading ${progress.pct}%` : "Add screenshots / recordings"}
      </Button>
      {!progress && <span className="ml-2 text-[10px] font-mono text-neutral-500" data-testid={`${testid}-paste-hint`}>or paste a screenshot (Ctrl+V / ⌘V)</span>}
      {progress && (
        <div className="h-1.5 rounded bg-neutral-100 overflow-hidden" data-testid={`${testid}-progress`}>
          <div className="h-full bg-orange-500 transition-[width] duration-300" style={{ width: `${progress.pct}%` }} />
        </div>
      )}
      <BugMediaGallery items={value} onRemove={(i) => onChange(value.filter((_, idx) => idx !== i))} testid={testid} />
      <ScreenshotMarkup file={queue[0] || null} onDone={attachMarked} onSkip={attachMarked} onCancel={nextInQueue} />
    </div>
  );
}

export function BugMediaGallery({ items = [], onRemove, testid = "bug-gallery" }) {
  const [view, setView] = useState(null);
  if (!items.length) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {items.map((a, i) => (
          <div key={`${a.path}-${i}`} className="relative group">
            <button type="button" onClick={() => kindOf(a) === "file" ? window.open(fileUrl(a.path), "_blank") : setView(a)}
              className="w-20 h-16 rounded-md border border-neutral-200 bg-neutral-50 overflow-hidden grid place-items-center hover:border-orange-400 transition-colors"
              title={a.filename} data-testid={`${testid}-item-${i}`}>
              {kindOf(a) === "image" && <img src={fileUrl(a.path)} alt={a.filename} className="w-full h-full object-cover" loading="lazy" />}
              {kindOf(a) === "video" && <span className="flex flex-col items-center text-[9px] font-mono text-neutral-600"><Film className="w-5 h-5 text-violet-600" />Recording</span>}
              {kindOf(a) === "file" && <Paperclip className="w-4 h-4 text-neutral-500" />}
            </button>
            {onRemove && (
              <button type="button" onClick={() => onRemove(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-neutral-300 grid place-items-center text-neutral-600 hover:text-rose-600" data-testid={`${testid}-remove-${i}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-4xl bg-white p-3" data-testid="bug-media-viewer">
          <DialogTitle className="text-sm font-mono truncate pr-6">{view?.filename}</DialogTitle>
          {view && kindOf(view) === "image" && <img src={fileUrl(view.path)} alt={view.filename} className="max-h-[75vh] w-full object-contain rounded" />}
          {view && kindOf(view) === "video" && <video src={fileUrl(view.path)} controls autoPlay className="max-h-[75vh] w-full rounded bg-black" data-testid="bug-media-video" />}
        </DialogContent>
      </Dialog>
    </>
  );
}
